import React, { useState } from "react";
import { Save, Sparkles, Trophy, Plus, Trash2, Eye, Edit3, ExternalLink, UploadCloud, Download } from "lucide-react";
import { saveAboutContent } from "../../api";
import { useToast } from "../../hooks/use-toast";
import CsvUploadModal from "../../components/admin/CsvUploadModal";
import { toCsv, downloadCsv } from "../../utils/csvHelper";

const AboutContentEditor = ({ initialContent, onUpdate }) => {
  const { toast } = useToast();
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [content, setContent] = useState(
    initialContent || {
      heroBadge: "ABOUT MINIGOLF USA",
      heroTitle: "Connecting America with the Joy of Miniature Golf",
      heroSubtitle: "Your premier directory for discovering over 4,000 top-rated mini golf courses across all 50 states.",
      storyTitle: "Our Mission & Story",
      storyParagraphs: [
        "Mini Golf USA was born out of a simple passion: making it easy for families, couples, travelers, and mini golf enthusiasts to find incredible miniature golf experiences wherever they are in the country.",
        "From vintage 1950s obstacle courses with spinning windmills to multi-million dollar adventure courses featuring pirate ships, erupting volcanoes, and glow-in-the-dark blacklight rooms, miniature golf remains one of the most accessible and joyful pastimes in American culture.",
      ],
      stats: [
        { number: "4,000+", label: "Courses Listed", description: "Verified courses nationwide" },
        { number: "50", label: "States Covered", description: "Every state and major metro area" },
        { number: "250K+", label: "Monthly Putters", description: "Players discovering new courses" },
        { number: "4.8★", label: "Average Rating", description: "Authentic visitor reviews" },
      ],
      valuesTitle: "What Sets Us Apart",
      values: [
        {
          icon: "Trophy",
          title: "Verified & Accurate Listings",
          description: "Every course profile includes accurate operating hours, direct phone numbers, admission rates, and genuine visitor reviews.",
        },
        {
          icon: "Compass",
          title: "Comprehensive Geography",
          description: "From coastal resort greens in Florida and California to indoor climate-controlled courses in the Midwest, we cover every corner of the nation.",
        },
      ],
      ctaTitle: "Ready to Plan Your Next Round?",
      ctaSubtitle: "Browse courses by state, explore top-rated destinations, or read our expert guides.",
    }
  );

  const [activeTab, setActiveTab] = useState("edit"); // "edit" or "preview"
  const [storyText, setStoryText] = useState((content.storyParagraphs || []).join("\n\n"));
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...content,
        storyParagraphs: storyText.split(/\n\n+/).map((p) => p.trim()).filter(Boolean),
      };
      const saved = await saveAboutContent(payload);
      setContent(saved);
      if (onUpdate) onUpdate(saved);
      toast({
        title: "About Us Page Saved",
        description: "Your changes are now live on the website.",
      });
    } catch (err) {
      toast({
        title: "Save Failed",
        description: err.message || "Failed to update About Us content.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExportCsv = () => {
    const rows = [];
    (content.stats || []).forEach((s) => {
      rows.push({ type: "stat", title: s.number, description: s.label, extra: s.description });
    });
    (content.values || []).forEach((v) => {
      rows.push({ type: "value", title: v.title, description: v.description, extra: v.icon });
    });
    (content.storyParagraphs || []).forEach((p, idx) => {
      rows.push({ type: "story", title: `Story Paragraph ${idx + 1}`, description: p, extra: "" });
    });

    const cols = [
      { key: "type", label: "Type" },
      { key: "title", label: "Title / Number / Heading" },
      { key: "description", label: "Description / Label" },
      { key: "extra", label: "Extra / Subtitle / Icon" },
    ];
    const csv = toCsv(rows, cols);
    downloadCsv("about_us_content.csv", csv);
    toast({ title: "About Content Exported", description: `Exported ${rows.length} records to CSV.` });
  };

  const handleImportCsv = async (validItems, rawRows, mode) => {
    const statItems = validItems.filter((item) => item.type === "stat");
    const valueItems = validItems.filter((item) => item.type === "value");
    const storyItems = validItems.filter((item) => item.type === "story");

    const newStats = statItems.map((item) => ({
      number: item.title,
      label: item.description,
      description: item.extra || "",
    }));

    const newValues = valueItems.map((item) => ({
      title: item.title,
      description: item.description,
      icon: item.extra || "Trophy",
    }));

    const newStories = storyItems.map((item) => item.description);

    setContent((prev) => {
      const updatedStats =
        newStats.length > 0
          ? mode === "replace"
            ? newStats
            : [...(prev.stats || []), ...newStats]
          : prev.stats;

      const updatedValues =
        newValues.length > 0
          ? mode === "replace"
            ? newValues
            : [...(prev.values || []), ...newValues]
          : prev.values;

      const updatedStories =
        newStories.length > 0
          ? mode === "replace"
            ? newStories
            : [...(prev.storyParagraphs || []), ...newStories]
          : prev.storyParagraphs;

      if (newStories.length > 0) {
        setStoryText(updatedStories.join("\n\n"));
      }

      return {
        ...prev,
        stats: updatedStats,
        values: updatedValues,
        storyParagraphs: updatedStories,
      };
    });

    toast({
      title: "About Us Data Updated",
      description: "Imported CSV elements. Click 'Save All Changes' to make them live.",
    });
  };

  const handleAddStat = () => {
    setContent({
      ...content,
      stats: [...(content.stats || []), { number: "100+", label: "New Stat", description: "Stat description" }],
    });
  };

  const handleRemoveStat = (index) => {
    setContent({
      ...content,
      stats: content.stats.filter((_, idx) => idx !== index),
    });
  };

  const handleStatChange = (index, field, value) => {
    const updated = [...content.stats];
    updated[index] = { ...updated[index], [field]: value };
    setContent({ ...content, stats: updated });
  };

  const handleAddValue = () => {
    setContent({
      ...content,
      values: [
        ...(content.values || []),
        { icon: "Trophy", title: "New Feature / Value", description: "Explain why this matters to players." },
      ],
    });
  };

  const handleRemoveValue = (index) => {
    setContent({
      ...content,
      values: content.values.filter((_, idx) => idx !== index),
    });
  };

  const handleValueChange = (index, field, value) => {
    const updated = [...content.values];
    updated[index] = { ...updated[index], [field]: value };
    setContent({ ...content, values: updated });
  };

  const inputCls =
    "w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e5243b] bg-white";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">About Us Page Content</h1>
          <p className="text-xs text-gray-500 mt-1">
            Customize hero headline, story paragraphs, stats counters, core values, and call-to-action
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/about"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-xl transition-colors shadow-sm"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View Live Page
          </a>
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === "edit" ? "preview" : "edit")}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-xl transition-colors shadow-sm"
          >
            {activeTab === "edit" ? <Eye className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
            {activeTab === "edit" ? "Live Preview" : "Form Editor"}
          </button>

          <button
            type="button"
            onClick={handleExportCsv}
            title="Export Stats & Values to CSV"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-xl transition-colors shadow-sm"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>

          <button
            type="button"
            onClick={() => setCsvModalOpen(true)}
            title="Import Stats & Values from Excel, CSV, TSV, or JSON"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors shadow-sm"
          >
            <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl shadow-md transition-all active:scale-[0.98] disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {activeTab === "preview" ? (
        /* Preview Layout */
        <div className="bg-[#0d0e12] rounded-2xl p-8 text-gray-100 border border-gray-800 shadow-2xl space-y-8">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/20 mb-3">
              {content.heroBadge}
            </div>
            <h2 className="text-3xl font-black text-white">{content.heroTitle}</h2>
            <p className="text-gray-300 text-sm mt-3">{content.heroSubtitle}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {(content.stats || []).map((s, idx) => (
              <div key={idx} className="bg-[#181a22] border border-gray-800 rounded-xl p-4 text-center">
                <div className="text-2xl font-black text-[#e5243b]">{s.number}</div>
                <div className="text-xs font-bold text-white mt-1">{s.label}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">{s.description}</div>
              </div>
            ))}
          </div>

          <div className="bg-[#181a22] border border-gray-800 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-3">{content.storyTitle}</h3>
            <div className="space-y-3 text-sm text-gray-300 leading-relaxed">
              {storyText.split(/\n\n+/).filter(Boolean).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Form Editor */
        <form onSubmit={handleSave} className="space-y-8 max-w-4xl">
          {/* Hero Section Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#e5243b]" /> Hero Header Section
            </h2>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Top Badge Text
              </label>
              <input
                type="text"
                value={content.heroBadge || ""}
                onChange={(e) => setContent({ ...content, heroBadge: e.target.value })}
                placeholder="ABOUT MINIGOLF USA"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Main Headline (H1)
              </label>
              <input
                type="text"
                value={content.heroTitle || ""}
                onChange={(e) => setContent({ ...content, heroTitle: e.target.value })}
                placeholder="Connecting America with the Joy of Miniature Golf"
                className={inputCls}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Hero Subtitle / Description
              </label>
              <textarea
                rows={3}
                value={content.heroSubtitle || ""}
                onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                className={inputCls}
                required
              />
            </div>
          </div>

          {/* Stats Counters Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> Stats Banner Items ({(content.stats || []).length})
              </h2>
              <button
                type="button"
                onClick={handleAddStat}
                className="inline-flex items-center gap-1 text-xs font-bold text-[#e5243b] hover:text-[#cf1c31]"
              >
                <Plus className="w-3.5 h-3.5" /> Add Stat Item
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(content.stats || []).map((stat, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4 relative group">
                  <button
                    type="button"
                    onClick={() => handleRemoveStat(idx)}
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-500 p-1 rounded-lg"
                    title="Remove stat"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                        Number / Value
                      </label>
                      <input
                        type="text"
                        value={stat.number}
                        onChange={(e) => handleStatChange(idx, "number", e.target.value)}
                        placeholder="4,000+"
                        className="w-full px-3 py-1.5 text-sm font-extrabold text-[#e5243b] border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                        Label Title
                      </label>
                      <input
                        type="text"
                        value={stat.label}
                        onChange={(e) => handleStatChange(idx, "label", e.target.value)}
                        placeholder="Courses Listed"
                        className="w-full px-3 py-1.5 text-xs font-semibold text-gray-800 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                        Sub-Description
                      </label>
                      <input
                        type="text"
                        value={stat.description || ""}
                        onChange={(e) => handleStatChange(idx, "description", e.target.value)}
                        placeholder="Verified courses nationwide"
                        className="w-full px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Story & Mission Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3">
              Mission & Story Section
            </h2>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Story Heading (H2)
              </label>
              <input
                type="text"
                value={content.storyTitle || ""}
                onChange={(e) => setContent({ ...content, storyTitle: e.target.value })}
                placeholder="Our Mission & Story"
                className={inputCls}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Story Paragraphs (separate paragraphs with a blank line)
              </label>
              <textarea
                rows={6}
                value={storyText}
                onChange={(e) => setStoryText(e.target.value)}
                className={`${inputCls} font-mono text-xs leading-relaxed`}
                required
              />
            </div>
          </div>

          {/* Core Values Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-gray-900">
                Core Values & Features ({(content.values || []).length})
              </h2>
              <button
                type="button"
                onClick={handleAddValue}
                className="inline-flex items-center gap-1 text-xs font-bold text-[#e5243b] hover:text-[#cf1c31]"
              >
                <Plus className="w-3.5 h-3.5" /> Add Value Card
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(content.values || []).map((val, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4 relative">
                  <button
                    type="button"
                    onClick={() => handleRemoveValue(idx)}
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-500 p-1 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                        Icon Name (Trophy, Compass, Users, Heart, Star)
                      </label>
                      <input
                        type="text"
                        value={val.icon}
                        onChange={(e) => handleValueChange(idx, "icon", e.target.value)}
                        placeholder="Trophy"
                        className="w-full px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        value={val.title}
                        onChange={(e) => handleValueChange(idx, "title", e.target.value)}
                        placeholder="Verified & Accurate Listings"
                        className="w-full px-3 py-1.5 text-xs font-bold border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={2}
                        value={val.description}
                        onChange={(e) => handleValueChange(idx, "description", e.target.value)}
                        className="w-full px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Banner Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3">
              Bottom Call To Action (CTA) Section
            </h2>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                CTA Headline
              </label>
              <input
                type="text"
                value={content.ctaTitle || ""}
                onChange={(e) => setContent({ ...content, ctaTitle: e.target.value })}
                placeholder="Ready to Plan Your Next Round?"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                CTA Subtitle
              </label>
              <input
                type="text"
                value={content.ctaSubtitle || ""}
                onChange={(e) => setContent({ ...content, ctaSubtitle: e.target.value })}
                placeholder="Browse courses by state, explore top-rated destinations..."
                className={inputCls}
              />
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 rounded-xl font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] shadow-lg shadow-red-900/20 transition-all flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving Changes..." : "Save All Changes"}
            </button>
          </div>
        </form>
      )}

      <CsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        sectionKey="about"
        onImport={handleImportCsv}
        allowModeSwitch={true}
      />
    </div>
  );
};

export default AboutContentEditor;
